import { Check } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export interface MultiSelectItem {
  id: string;
  label: string;
}

type Accent = "blue" | "purple" | "violet" | "green";

const accentClasses: Record<Accent, string> = {
  blue: "hover:bg-blue-50 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500",
  purple: "hover:bg-purple-50 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500",
  violet: "hover:bg-violet-50 data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500",
  green: "hover:bg-green-50 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500",
};

interface MultiSelectPopoverProps {
  items: MultiSelectItem[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  accent?: Accent;
  disabled?: boolean;
  className?: string;
}

/**
 * Checkbox multi-select inside a popover. Mirrors the filter/bucket pickers used
 * on the manual scanning page so every multi-select in the app behaves the same.
 */
export function MultiSelectPopover({
  items,
  value,
  onChange,
  placeholder = "Select options",
  accent = "blue",
  disabled = false,
  className = "w-full justify-between h-11 bg-white border-2 border-gray-200 hover:border-blue-300 rounded-xl",
}: MultiSelectPopoverProps) {
  const classes = accentClasses[accent];

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  const selectedLabels = items.filter((item) => value.includes(item.id)).map((item) => item.label);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className={className}
        >
          <span className="text-left truncate">
            {value.length === 0
              ? placeholder
              : selectedLabels.length <= 2
              ? selectedLabels.join(", ")
              : `Selected (${value.length})`}
          </span>
          <Check className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-3 max-h-80 overflow-y-auto bg-white rounded-xl shadow-xl">
        <div className="flex justify-between mb-2">
          <Button
            type="button"
            variant="ghost"
            className="text-sm"
            onClick={() => onChange(items.map((item) => item.id))}
          >
            Select All
          </Button>
          <Button type="button" variant="ghost" className="text-sm" onClick={() => onChange([])}>
            Clear
          </Button>
        </div>
        <div className="space-y-2">
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-3">No records found</p>
          )}
          {items.map((item) => {
            const isChecked = value.includes(item.id);
            return (
              <div
                key={item.id}
                className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer border border-transparent transition-all ${classes}`}
                onClick={() => toggle(item.id)}
              >
                <Checkbox checked={isChecked} onCheckedChange={() => toggle(item.id)} className={classes} />
                <span className="font-medium text-gray-700 text-sm">{item.label}</span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
